import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import { motion } from "framer-motion";
import { ArrowRight, AirplaneTilt, Buildings, MapPin, Camera, Lightning, Heart, ChatCircle } from "@phosphor-icons/react";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/7bfd5dde-59f4-4f41-bd29-ac397f2276b1/images/7bcee18c88ed6ebeb3ff43d2eadfc3ba1d6b71417f446e09cad32cfb3e4b3552.png";
const INDIA_IMG = "https://images.unsplash.com/photo-1727686669949-4d497a86ed9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxJbmRpYSUyMHRyYXZlbCUyMGxhbmRtYXJrc3xlbnwwfHx8fDE3Nzc3MDgzMzZ8MA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
    { icon: AirplaneTilt, label: "Flights", desc: "Best routes, layovers & price tips for SFO/LAX → India." },
    { icon: Buildings, label: "Hotels", desc: "Trusted stays curated by Trojans who've been there." },
    { icon: MapPin, label: "Destinations", desc: "From Goa beaches to Ladakh peaks – find your trip." },
    { icon: Camera, label: "Activities", desc: "Food crawls, treks, festivals & hidden gems." },
];

export default function LandingPage() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
            <Navbar />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#990000]/85 via-[#7A0000]/75 to-black/70" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-28 sm:pt-28 sm:pb-36">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFCC00] text-[#5C0000] text-xs tracking-[0.2em] uppercase font-bold mb-6">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#990000]" />
                            For Trojans · By Trojans
                        </div>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter leading-[0.95] mb-6">
                            Travel India,<br />
                            the <span className="text-[#FFCC00]">Trojan</span> way.
                        </h1>
                        <p className="text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed mb-10">
                            A community portal where USC alumni share real flight deals, trusted hotels,
                            destinations and things to do across India — every recommendation backed by a
                            fellow Trojan.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Button
                                data-testid="hero-cta-join-btn"
                                onClick={() => navigate("/register")}
                                className="bg-[#FFCC00] text-[#5C0000] hover:bg-[#E6B800] h-12 px-7 text-base font-semibold rounded-lg"
                            >
                                Join the Community
                                <ArrowRight size={18} weight="bold" className="ml-2" />
                            </Button>
                            <Button
                                data-testid="hero-cta-login-btn"
                                onClick={() => navigate("/login")}
                                variant="outline"
                                className="border-2 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-7 text-base font-semibold rounded-lg"
                            >
                                Sign in
                            </Button>
                        </div>

                        <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
                            {[
                                { v: "4", l: "Categories" },
                                { v: "1+", l: "USC alumni" },
                                { v: "∞", l: "Trip ideas" },
                            ].map((s) => (
                                <div key={s.l}>
                                    <div className="font-heading text-3xl text-[#FFCC00] font-bold">{s.v}</div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
                <div className="absolute -bottom-px left-0 right-0 h-12 bg-gradient-to-t from-[#FAFAFA] to-transparent" />
            </section>

            {/* Categories */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">
                            What Trojans share
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A] max-w-xl">
                            Every category you need for an India trip
                        </h2>
                    </div>
                    <Link to="/feed" className="text-sm text-[#990000] font-semibold hover:underline">
                        Browse all posts →
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {CATEGORIES.map((c, i) => (
                        <motion.div
                            key={c.label}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.08 }}
                            data-testid={`landing-category-${c.label.toLowerCase()}`}
                            className="group bg-white border border-stone-200 rounded-xl p-7 hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(153,0,0,0.10)] hover:border-[#990000]/30 transition-all duration-300 cursor-pointer"
                            onClick={() => navigate("/feed")}
                        >
                            <div className="w-12 h-12 rounded-lg bg-[#990000]/5 flex items-center justify-center mb-5 group-hover:bg-[#990000] transition-colors">
                                <c.icon size={24} className="text-[#990000] group-hover:text-[#FFCC00] transition-colors" weight="regular" />
                            </div>
                            <div className="font-heading text-xl font-semibold text-[#0A0A0A] mb-2">{c.label}</div>
                            <div className="text-sm text-stone-500 leading-relaxed">{c.desc}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Why join */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5 order-2 lg:order-1">
                        <div className="relative rounded-2xl overflow-hidden border border-stone-200">
                            <img src={INDIA_IMG} alt="India travel" className="w-full h-[420px] object-cover" />
                            <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 border border-white">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Featured</div>
                                <div className="font-heading text-base font-semibold text-[#0A0A0A]">Wayanad, Kerala</div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7 order-1 lg:order-2">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-3">
                            Why TrojanTrails
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-6">
                            Real recs from your USC family — not algorithms.
                        </h2>
                        <div className="space-y-5">
                            {[
                                { icon: Lightning, t: "Curated by alumni", d: "Every flight, hotel, and activity tip comes straight from a fellow Trojan who's been there." },
                                { icon: Heart, t: "Upvote what you love", d: "The best recommendations rise to the top so you find them in seconds." },
                                { icon: ChatCircle, t: "Ask follow-up questions", d: "Comment on any post — get insider answers from the original poster." },
                            ].map((item) => (
                                <div key={item.t} className="flex gap-4">
                                    <div className="w-10 h-10 rounded-lg bg-[#FFF4CC] border border-[#FFE680] flex items-center justify-center flex-shrink-0">
                                        <item.icon size={18} className="text-[#7A0000]" weight="regular" />
                                    </div>
                                    <div>
                                        <div className="font-heading font-semibold text-[#0A0A0A] text-lg mb-1">{item.t}</div>
                                        <div className="text-sm text-stone-600 leading-relaxed">{item.d}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Button
                            data-testid="why-section-cta-btn"
                            onClick={() => navigate("/register")}
                            className="mt-8 bg-[#990000] text-white hover:bg-[#7A0000] h-11 px-6 font-medium"
                        >
                            Get started — it's free
                            <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-stone-200 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-md bg-[#990000] flex items-center justify-center">
                            <span className="text-[#FFCC00] font-heading font-bold text-sm">T</span>
                        </div>
                        <span className="font-heading font-semibold text-[#0A0A0A]">TrojanTrails: India</span>
                    </div>
                    <div className="text-xs text-stone-500 font-mono uppercase tracking-[0.2em]">
                        Fight On · Travel On
                    </div>
                </div>
            </footer>
        </div>
    );
}
